/** Browser mechanics for server-supplied result targets; no choice, count or page policy. */
import { createContext, useContext, type ReactNode } from 'react'
import { productLanguage } from '@testfeed/product-language'
import type { ShopifyNavigationClick } from '../shopifyAppNavigationClick'

type AnswerRead = { status: 'ready' | 'loading' | 'error'; message: string; retry: () => void }
export type ResultNavigation = {
  navigate: (event: ShopifyNavigationClick, href: string, position: 'answers' | 'top') => void
  answers: AnswerRead
}
export const ResultNavigationContext = createContext<ResultNavigation | null>(null)
export const useResultNavigation = () => useContext(ResultNavigationContext)

/** A real link for modified clicks, with an explicit client-navigation target for normal activation. */
export function ResultNavigationButton({
  href,
  position = 'top',
  slot,
  children,
}: {
  href: string
  position?: 'answers' | 'top'
  slot?: 'secondary-actions'
  children: ReactNode
}) {
  const navigation = useResultNavigation()
  return (
    <s-button href={href} slot={slot} onClick={(event) => navigation?.navigate(event, href, position)}>
      {children}
    </s-button>
  )
}

/** Retain the reading-space geometry while refusing to display old rows as a newly requested page. */
export function ResultAnswerRegion({ children, loading }: { children: ReactNode; loading: ReactNode }) {
  const navigation = useResultNavigation()
  const read = navigation?.answers
  const waiting = read !== undefined && read.status !== 'ready'
  let feedback = null
  let feedbackRegion = null
  let feedbackOverflow: 'hidden' | 'visible' = 'visible'
  let loadingLabel: string | undefined
  if (read?.status === 'loading') {
    feedback = loading
    loadingLabel = productLanguage.shopify.studyQuestions.results.loadingAnswers
    feedbackOverflow = 'hidden'
  }
  if (read?.status === 'error')
    feedback = (
      <s-banner tone="critical">
        <s-text>{read.message}</s-text>
        <s-button onClick={read.retry}>Try again</s-button>
      </s-banner>
    )
  if (waiting)
    feedbackRegion = (
      <div
        role="status"
        aria-label={loadingLabel}
        style={{ position: 'absolute', inset: 0, overflow: feedbackOverflow }}
      >
        {feedback}
      </div>
    )
  return (
    <div
      id="result-authored-answers"
      tabIndex={-1}
      aria-busy={read?.status === 'loading'}
      style={{ position: 'relative', scrollMarginTop: 16 }}
    >
      <div aria-hidden={waiting} style={{ visibility: waiting ? 'hidden' : 'visible' }}>
        {children}
      </div>
      {feedbackRegion}
    </div>
  )
}
